import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, GraduationCap, AlertTriangle, ListTodo, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { projectAPI } from '@/utils/supabase/client';
import type { InternProfile, AdminTask, AdminBlocker } from '../types';
import {
  STATUS_LABEL, STATUS_BADGE, ACTIVE_TASK_STATUSES, partitionByDone,
  dueLabel, escalationClass, formatDate,
} from '../helpers';
import { CreateTaskDialog } from './CreateTaskDialog';
import { TaskDetailSheet } from './TaskDetailSheet';

const ALL = '__all__';

export function InternAdminPage() {
  const [interns, setInterns] = useState<InternProfile[]>([]);
  const [tasks, setTasks] = useState<AdminTask[]>([]);
  const [blockers, setBlockers] = useState<AdminBlocker[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigneeFilter, setAssigneeFilter] = useState<string>(ALL);
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [selected, setSelected] = useState<AdminTask | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [i, t, b] = await Promise.all([
        projectAPI.getInterns(),
        projectAPI.getInternTasks(),
        projectAPI.getInternBlockers(),
      ]);
      setInterns(i);
      setTasks(t);
      setBlockers(b);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load intern data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep the open sheet in sync with freshly loaded task rows.
  useEffect(() => {
    if (!selected) return;
    const fresh = tasks.find((t) => t.id === selected.id);
    if (fresh) setSelected(fresh);
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  // Done tasks move off the active list into their own tab (still viewable by both
  // the admin here and the intern on their Done page).
  const { active, done } = useMemo(() => partitionByDone(tasks), [tasks]);

  const matchesAssignee = useCallback((t: AdminTask) => {
    if (assigneeFilter === ALL) return true;
    return assigneeFilter === 'unassigned' ? !t.assignee_id : t.assignee_id === assigneeFilter;
  }, [assigneeFilter]);

  const visibleActive = useMemo(
    () => active.filter((t) => matchesAssignee(t) && (statusFilter === ALL || t.status === statusFilter)),
    [active, matchesAssignee, statusFilter],
  );

  const visibleDone = useMemo(
    () => done
      .filter(matchesAssignee)
      .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? '')),
    [done, matchesAssignee],
  );

  async function handleResolve(noteId: string) {
    setResolvingId(noteId);
    try {
      await projectAPI.resolveInternBlocker(noteId);
      toast.success('Blocker resolved');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resolve blocker');
    } finally {
      setResolvingId(null);
    }
  }

  function openTask(task: AdminTask) {
    setSelected(task);
    setSheetOpen(true);
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <GraduationCap className="size-5" />
          </div>
          <div>
            <h1 className="text-xl font-serif font-semibold tracking-tight">Interns</h1>
            <p className="text-sm text-muted-foreground">
              Assigned tasks, blockers, and feedback across the intern program.
            </p>
          </div>
        </div>
        <CreateTaskDialog interns={interns} onCreated={load} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks">Tasks ({active.length})</TabsTrigger>
          <TabsTrigger value="done">Done ({done.length})</TabsTrigger>
          <TabsTrigger value="blockers">
            Blockers{blockers.length > 0 ? ` (${blockers.length})` : ''}
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="mt-4">
          {interns.length === 0 ? (
            <EmptyState label="No interns yet." />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {interns.map((intern) => (
                <Card key={intern.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between text-base">
                      <span>{intern.display_name || intern.email}</span>
                      {intern.intern_track && (
                        <Badge variant="secondary" className="capitalize">{intern.intern_track}</Badge>
                      )}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">{intern.email}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <Stat icon={<ListTodo className="size-4" />} value={intern.counts.openTasks} label="Open" />
                      <Stat icon={<AlertTriangle className="size-4" />} value={intern.counts.overdue} label="Overdue" tone={intern.counts.overdue > 0 ? 'danger' : undefined} />
                      <Stat icon={<AlertTriangle className="size-4" />} value={intern.counts.openBlockers} label="Blockers" tone={intern.counts.openBlockers > 0 ? 'warning' : undefined} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Tasks (active) ── */}
        <TabsContent value="tasks" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <AssigneeFilter value={assigneeFilter} onChange={setAssigneeFilter} interns={interns} />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {ACTIVE_TASK_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {visibleActive.length === 0 ? (
            <EmptyState label="No active tasks match these filters." />
          ) : (
            <TaskTable tasks={visibleActive} onOpen={openTask} />
          )}
        </TabsContent>

        {/* ── Done ── */}
        <TabsContent value="done" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <AssigneeFilter value={assigneeFilter} onChange={setAssigneeFilter} interns={interns} />
          </div>

          {visibleDone.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="size-5 text-success" />}
              label="No completed tasks yet."
            />
          ) : (
            <TaskTable tasks={visibleDone} onOpen={openTask} />
          )}
        </TabsContent>

        {/* ── Blockers ── */}
        <TabsContent value="blockers" className="mt-4">
          {blockers.length === 0 ? (
            <EmptyState icon={<CheckCircle2 className="size-5 text-success" />} label="No open blockers. Nice." />
          ) : (
            <div className="space-y-3">
              {blockers.map((b) => (
                <Card key={b.id}>
                  <CardContent className="flex items-start justify-between gap-4 pt-6">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="destructive" className="text-[10px]">blocker</Badge>
                        <span className="text-sm font-medium truncate">{b.task?.title || 'Unknown task'}</span>
                      </div>
                      <p className="text-sm text-foreground/80 whitespace-pre-wrap">{b.body}</p>
                      <p className="text-xs text-muted-foreground">
                        {b.assignee_name || 'Unassigned'} · {formatDate(b.created_at)}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {b.task && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const t = tasks.find((x) => x.id === b.task!.id);
                            if (t) openTask(t);
                          }}
                        >
                          Open task
                        </Button>
                      )}
                      <Button size="sm" onClick={() => handleResolve(b.id)} disabled={resolvingId === b.id}>
                        {resolvingId === b.id ? <Loader2 className="size-4 animate-spin" /> : 'Resolve'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <TaskDetailSheet
        task={selected}
        interns={interns}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onChanged={load}
      />
    </div>
  );
}

function AssigneeFilter({ value, onChange, interns }: {
  value: string; onChange: (v: string) => void; interns: InternProfile[];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-48"><SelectValue placeholder="Assignee" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All assignees</SelectItem>
        <SelectItem value="unassigned">Unassigned</SelectItem>
        {interns.filter((i) => i.auth_user_id).map((i) => (
          <SelectItem key={i.id} value={i.auth_user_id as string}>{i.display_name || i.email}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function TaskTable({ tasks, onOpen }: { tasks: AdminTask[]; onOpen: (t: AdminTask) => void }) {
  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Task</TableHead>
            <TableHead>Assignee</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Due</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tasks.map((task) => (
            <TableRow key={task.id} className="cursor-pointer" onClick={() => onOpen(task)}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  {task.is_critical && <span className="size-1.5 rounded-full bg-destructive shrink-0" />}
                  <span className="line-clamp-1">{task.title}</span>
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {task.assignee_name || 'Unassigned'}
              </TableCell>
              <TableCell>
                <Badge variant={STATUS_BADGE[task.status]}>{STATUS_LABEL[task.status]}</Badge>
              </TableCell>
              <TableCell className={escalationClass(task.escalation)}>
                {dueLabel(task)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function Stat({ icon, value, label, tone }: {
  icon: React.ReactNode; value: number; label: string; tone?: 'danger' | 'warning';
}) {
  const color = tone === 'danger' ? 'text-destructive' : tone === 'warning' ? 'text-warning' : 'text-foreground';
  return (
    <div className="rounded-md bg-muted/40 py-2">
      <div className={`flex items-center justify-center gap-1 text-lg font-semibold ${color}`}>
        {icon}{value}
      </div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

function EmptyState({ label, icon }: { label: string; icon?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-12 text-center">
      {icon}
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
